import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import {
  CdkDragDrop,
  CdkDropList,
  CdkDrag,
  moveItemInArray,
  transferArrayItem,
  CdkDropListGroup
} from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';

import {
  Board,
  BoardCard,
  BoardCardRegistryService,
  BoardCardType,
  BoardJSON
} from '@fry/boards/lib';
import { BoardLayoutEditorCardComponent } from './board-layout-editor-card.component';

@Component({
    selector: 'eas-board-layout-editor',
    templateUrl: './board-layout-editor.component.html',
    styleUrls: ['./board-layout-editor.component.css'],
    imports: [
        BoardLayoutEditorCardComponent,
        CdkDrag,
        CdkDropList,
        CdkDropListGroup,
        FormsModule,
        MatButtonModule,
        MatCardModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatSelectModule,
        RouterModule,
    ],
    providers: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BoardLayoutEditorComponent implements OnInit, OnDestroy {

  public boardCardRegistry = inject(BoardCardRegistryService);

  public board?: Board;
  public boardJSON = input.required<BoardJSON>();

  public title = input.required<string>();

  private dialog = inject(MatDialog);
  private formDialogRef: MatDialogRef<any, any> | undefined;
  public changed = output<BoardJSON>();

  constructor() {
  }

  ngOnInit() {
    this.board = this.boardCardRegistry.boardFromJson(this.boardJSON());
  }

  ngOnDestroy(): void { }

  //
  // Actions
  //

  public onRemoveCard(id: string): void {
    if (!this.board) { throw new Error('Board is not defined!'); }

    this.board.removeCardById(id);
    this.changed.emit(this.board.toJSON());
  }

  public onChangeCardSettings(id: string): void {
    if (!this.board) { throw new Error('Board is not defined!'); }

    const card = this.board.findCardById(id);
    if (!card) { return; }
    if (!card.type.editComponent) { return; }

    this.presentCardSettingsDialog(card, (result) => {
      if (!this.board) { throw new Error('Board is not defined!'); }
      if (!result || typeof result === 'string') { return; }

      this.board.replaceCard(card.id, new BoardCard(card.type, result, card.id));
      this.changed.emit(this.board!.toJSON());
    });
  }

  public onAddCard(type: BoardCardType): void {
    if (!this.board) { throw new Error('Board is not defined!'); }

    if (!type.editComponent) {
      const card = new BoardCard(type, { title: type.title });
      this.board?.addCard(0, card);
      this.changed.emit(this.board.toJSON());
      return;
    }

    const card = new BoardCard(type, { title: 'New Widget' });
    this.presentCardSettingsDialog(card, (result) => {
      if (!this.board) { throw new Error('Board is not defined!'); }
      if (!result || typeof result === 'string') { return; }

      this.board?.addCard(0, new BoardCard(type, result, card.id));
      this.changed.emit(this.board.toJSON());
    });
  }

  onDrop(event: CdkDragDrop<BoardCard[], any, any>) {
    if (!this.board) { throw new Error('Board is not defined!'); }

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
    this.changed.emit(this.board.toJSON());
  }

  //
  // Template helpers
  //

  private presentCardSettingsDialog(item: BoardCard,
                               afterClosed?: (result: any) => void): void {
    if (this.formDialogRef) { return; }

    const options: MatDialogConfig<any> = {
      minWidth: '50vw',
      minHeight: '60vh',
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      id: 'board-editor-form-dialog',
      data: {
        options: item.options,
        card: item
      }
    };

    this.formDialogRef =
      this.dialog.open(item!.type.editComponent!, options);
    this.formDialogRef.afterClosed().subscribe(result => {
      this.formDialogRef = undefined;
      if (afterClosed && result) { afterClosed(result); }
    });
  }
}
